/**
 * This file is part of Magestore POS Open Source.
 *
 * Magestore POS Open Source is free software: you can redistribute it
 * and/or modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation, either version 3
 * of the License, or (at your option) any later version.
 *
 * Magestore POS Open Source is distributed in the hope that it will
 * be useful, but WITHOUT ANY WARRANTY; without even the implied warranty
 * of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Magestore POS Open Source. If not, see <https://www.gnu.org/licenses/>
 */
import AbstractResourceModel from "../AbstractResourceModel";
import DateTimeHelper from "../../helper/DateTimeHelper";
import QueryService from "../../service/QueryService";
import ConfigHelper from "../../helper/ConfigHelper";
import OrderConstant from "../../view/constant/OrderConstant";
import SyncConstant from "../../view/constant/SyncConstant";
import ActionLogService from "../../service/sync/ActionLogService";
import StatusConstant from "../../view/constant/order/StatusConstant";
import {fire} from "../../event-bus";

export default class OrderResourceModel extends AbstractResourceModel {
    static className = 'OrderResourceModel';

    /**
     * Constructor
     * @param props
     */
    constructor(props) {
        super(props);
        this.state = {
            resourceName: 'Order',
            dataType: SyncConstant.TYPE_ORDER
        };
    }

    /**
     * save order to indexDb after place order
     * @param order
     */
    placeOrder(order) {
        return this.saveToDb([order], true).then(() => {
            return order;
        });
    }

    /**
     * Get date of order from since date
     */
    getConfigSinceDate() {
        let date = new Date();
        let configOrderSince = ConfigHelper.getConfig(OrderConstant.XML_PATH_CONFIG_SYNC_ORDER_SINCE);

        switch (configOrderSince) {
            case OrderConstant.ORDER_SINCE_24H:
                date.setDate(date.getDate() - 1);
                break;
            case OrderConstant.ORDER_SINCE_7_DAYS:
                date = new Date(date.getFullYear(), date.getMonth(), date.getDate() - 6);
                break;
            case OrderConstant.ORDER_SINCE_MONTH:
                date = new Date(date.getFullYear(), date.getMonth(), 1);
                break;
            case OrderConstant.ORDER_SINCE_YTD:
                date = new Date(date.getFullYear(), 0, 1);
                break;
            case OrderConstant.ORDER_SINCE_2_YTD:
                date = new Date(date.getFullYear() - 1, 0, 1);
                break;
            default:
                date.setDate(date.getDate() - 1);
        }
        return date;
    }

    /**
     * get out date orders
     * @return {Promise<{ids: Array}>}
     */
    async getOutDateOrders() {
        let queryService = QueryService.reset();
        let date = this.getConfigSinceDate();
        date = DateTimeHelper.getDatabaseDateTime(date.getTime());
        queryService.addQueryString('');
        queryService.addFieldToFilter('created_at', date, 'lt');
        queryService.addFieldToFilter('state', StatusConstant.STATE_HOLDED, 'neq');
        let result;
        let response = {ids: []};
        result = await this.getResourceOffline().getList(queryService);
        result.items.map(order => response.ids.push(order.increment_id));
        return response;
    }

    /**
     * take payment
     * @param order
     * @return {Promise<T>}
     */
    takePayment(order) {
        return this.saveToDb([order]).then(() => {
            return order;
        });
    }

    /**
     * refund
     *
     * @param creditmemo
     * @return {Promise<*>}
     */
    async refund(creditmemo) {
        fire('resource_model_order_refund', {
            creditmemo: creditmemo,
        });
        let url = this.getResourceOnline().getPathCreateCreditmemo();
        await ActionLogService.createDataActionLog(
            SyncConstant.REQUEST_CREATE_CREDITMEMO_ORDER,
            url,
            SyncConstant.METHOD_POST,
            {
                creditmemo: creditmemo
            }
        );
        return creditmemo;
    }

    /**
     *
     * @param request
     * @return {*|{type: string, quote: *}}
     */
    directPayment(request) {
        let resourceOnline = this.getResourceOnline();
        /**
         *
         * @type {OmcOrder} resourceOnline
         */
        return resourceOnline.directPayment(request);
    }

    /**
     * get list order statuses
     * @return {Promise<any>}
     */
    getListOrderStatuses() {
        return this.getResourceOnline().getListOrderStatuses();
    }

    /**
     * get order by increment_id offline
     * @param increment_id
     * @returns {Promise.<any>|*}
     */
    getOrderByIncrementIdOffline(increment_id) {
        return this.getResourceOffline().getById(increment_id);
    }

    /**
     * get order by increment ids
     * @param ids
     * @returns {*|Promise}
     */
    getOrderByIncrementIds(ids) {
        return this.getResourceOffline().getOrderByIncrementIds(ids);
    }

    /**
     * get out of permission orders
     * @param queryService
     * @param isSync
     * @returns {*|Promise<any>}
     */
    getOutOfPermissionOrders(queryService = {}, isSync = false) {
        return this.getResourceOnline().getOutOfPermissionOrders(queryService, isSync);
    }

    /**
     *
     * @param orderIncrement
     * @return {*|Promise<any>}
     */
    loadOrderByIncrement(orderIncrement) {
        let resourceOnline = this.getResourceOnline();
        /**
         *
         * @type {OmcOrder} resourceOnline
         */
        return resourceOnline.loadOrderByIncrement(orderIncrement);
    }
}



// WEBPACK FOOTER //
// ./src/resource-model/order/OrderResourceModel.js